import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { CookiePolicyLink } from 'components/CookiePolicyLink'
import { OptionCard } from 'components/OptionCard'
import { PrivacyPolicyLink } from 'components/PrivacyPolicyLink'
import { TermsOfUseLink } from 'components/TermsOfUseLink'

import { useCohortInfo } from 'hooks/useCohortInfo'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useNextStep } from 'hooks/useNextStep'

import { TPageProps } from 'models/common.model'

import { CustomPageId } from 'page-constants'
import { Color, Language, OptionType } from 'root-constants'

import { SelectGenderBlessV3 as S } from './SelectGenderBlessV3.styles'
import { COHORT_TO_VERSION_MAP, GENDER_OPTIONS_BY_VERSION } from './constants'

export const SelectGenderBlessV3: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
  isFirstPage,
}) => {
  const { t } = useTranslation()
  const { cohortToUse } = useCohortInfo()

  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const [answer, setAnswer] = useState<string>('')

  const context =
    COHORT_TO_VERSION_MAP[cohortToUse] || COHORT_TO_VERSION_MAP.default
  const genderOptions = GENDER_OPTIONS_BY_VERSION[context]

  const handleContinue = useNextStep({
    pageId: CustomPageId.GENDER,
    question: t('onboarding.selectGenderBlessV3.title', {
      lng: Language.EN,
      context,
    }),
    nextPagePath,
    isFirstPage,
  })

  const optionProps = {
    name: pageId,
    type: OptionType.RADIO,
    onChange: (value: string) => {
      handleChange(value, handleContinue)
      setIsAnswersDisabled(true)
      setAnswer(value)
    },
  }

  return (
    <S.Container>
      <S.Title>
        <Trans
          i18nKey="onboarding.selectGenderBlessV3.title"
          context={context}
        />
      </S.Title>
      <S.Subtitle>
        <Trans
          i18nKey="onboarding.selectGenderBlessV3.subtitle"
          context={context}
        />
      </S.Subtitle>

      <S.CardsContainer>
        {genderOptions.map(({ value, text, imageUrl }) => (
          <OptionCard
            key={value}
            imageUrl={imageUrl}
            value={value}
            label={t(text)}
            checked={answer === value}
            disabled={isAnswersDisabled}
            $backgroundColor={Color.WHITE}
            {...optionProps}
          />
        ))}
      </S.CardsContainer>

      <S.Terms>
        <p>{t('commonComponents.termsTitle')}</p>
        <TermsOfUseLink translationKey="commonComponents.termsOfService" />
        <S.Separator />
        <PrivacyPolicyLink />
        <S.Separator />
        <CookiePolicyLink />
      </S.Terms>

      <S.TermsNote>{t('commonComponents.termsNote_v1')}</S.TermsNote>
    </S.Container>
  )
}
